// ** UI
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

// ** Components
import FadeInView from '@/components/common/FadeInView';
import UnderlineHoverCta from '@/components/common/UnderlineHoverCta';
import StrapiImageRenderer from '@/components/strapi/StrapiImageRenderer';
import ComponentSharedTextEditor from '@/components/strapi/Shared/ComponentSharedTextEditor';
import { BILD_TEXT_DEFAULTS_TYPED } from '@/components/common/defaults';

// ** Utils
import { buildPathFromRelationLink } from '@/utils/ui/navigationUtils';

// ** Types
import type { Theme } from '@mui/material/styles';
import type { StrapiComponentProps } from '@/types/strapi';
import type { ComponentBlocksBildTextBlock as BildTextBlockType } from '@/types/graphql';

type ComponentBlocksBildTextBlockProps = StrapiComponentProps<Omit<BildTextBlockType, 'bildTextVariant'>>;

const layoutSx = (_isImageRight: boolean) => (theme: Theme) => {
	const TWO_COL_MEDIA = `@media (min-width: ${theme.layout.twoColMinWidthPx}px)`;

	return {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'stretch',
		gap: 6,
		[TWO_COL_MEDIA]: {
			flexDirection: 'row',
			alignItems: 'center',
			gap: theme.layout.desktopColumnGap
		}
	};
};

const imageSx = (isImageRight: boolean) => (theme: Theme) => {
	const TWO_COL_MEDIA = `@media (min-width: ${theme.layout.twoColMinWidthPx}px)`;

	return {
		position: 'relative',
		width: '100%',
		height: { xs: 260, sm: 320 },
		borderRadius: 2,
		overflow: 'hidden',
		minWidth: 0,
		[TWO_COL_MEDIA]: {
			order: isImageRight ? 2 : 1,
			width: `clamp(300px, 41.1vw, ${theme.layout.heroPanelContentMaxWidthPx}px)`,
			flex: '0 1 auto',
			height: 'auto',
			aspectRatio: `${theme.layout.heroPanelContentMaxWidthPx} / 392`
		}
	};
};

const textBlockSx = (isImageRight: boolean) => (theme: Theme) => {
	const TWO_COL_MEDIA = `@media (min-width: ${theme.layout.twoColMinWidthPx}px)`;

	return {
		minWidth: 0,
		[TWO_COL_MEDIA]: {
			order: isImageRight ? 1 : 2,
			flex: '1 1 0',
			maxWidth: 560,
			pt: 0
		}
	};
};

const SimpleBlock = ({
	host,
	bildTextImage,
	bildTextEyebrow = BILD_TEXT_DEFAULTS_TYPED.eyebrow,
	bildTextTitle = BILD_TEXT_DEFAULTS_TYPED.title,
	bildTextCopy = BILD_TEXT_DEFAULTS_TYPED.copy,
	bildTextImagePosition,
	bildTextContentVariant,
	bildTextContent,
	bildTextCta
}: ComponentBlocksBildTextBlockProps) => {
	const theme = useTheme();
	const themeMode = theme.palette.mode;

	const editor = bildTextContent;
	const isImageRight = bildTextImagePosition?.toLocaleLowerCase() === 'right';
	const withCta = !!bildTextCta;
	const ctaLink = bildTextCta?.hrefExternal ? bildTextCta?.hrefExternal : buildPathFromRelationLink(bildTextCta?.href);

	// 1. Determine the color theme (Simple variant colors)
	const textColor = theme.palette.text.secondary ?? theme.palette.text.primary;
	const titleColor = theme.palette.primary.main;

	// 2. Define the "Dynamic Copy" part
	const renderCopy = () => {
		if (bildTextContentVariant?.toLocaleLowerCase() === 'normal') {
			return (
				<Typography variant="body-md" sx={{ color: textColor, whiteSpace: 'pre-line' }}>
					{bildTextCopy}
				</Typography>
			);
		}

		return (
			<ComponentSharedTextEditor
				blockContent={editor?.blockContent || bildTextCopy}
				variantBody={editor?.variantBody}
				variantHeading1={editor?.variantHeading1}
				variantHeading2={editor?.variantHeading2}
				variantHeading3={editor?.variantHeading3}
				variantHeading4={editor?.variantHeading4}
				variantHeading5={editor?.variantHeading5}
				variantHeading6={editor?.variantHeading6}
				overrides={{
					paragraph: {
						mt: 0,
						color: textColor,
						fontFamily: '"DIN Next LT Pro"',
						fontSize: 'clamp(1rem, 0.9554rem + 0.2232vw, 1.125rem)',
						fontWeight: 400,
						lineHeight: 'clamp(1.375rem, 1.2857rem + 0.4464vw, 1.625rem)',
						letterSpacing: '-0.05px',
						[theme.breakpoints.up('md')]: {
							fontSize: 'clamp(1rem, 0.9554rem + 0.2232vw, 1.125rem)'
						}
					},
					heading: {
						fontFamily: '"DIN Next LT Pro"',
						color: textColor,
						mb: 1
					},
					list: {
						color: textColor,
						pl: 4,
						'& li': {
							p: 0.3,
							fontFamily: '"DIN Next LT Pro"',
							fontSize: 'clamp(1rem, 0.9554rem + 0.2232vw, 1.125rem)'
						}
					},
					listItem: {
						fontFamily: '"DIN Next LT Pro"',
						fontSize: 'clamp(1rem, 0.9554rem + 0.2232vw, 1.125rem)',
						color: textColor,
						'& .MuiTypography-root': {
							color: textColor,
							fontSize: 'inherit'
						},
						[theme.breakpoints.up('md')]: {
							fontSize: 'clamp(1rem, 0.9554rem + 0.2232vw, 1.125rem)'
						}
					},
					link: {
						color: textColor,
						textDecoration: 'underline',
						fontFamily: '"DIN Next LT Pro"',
						'&:hover': {
							opacity: 0.8
						},
						[theme.breakpoints.up('md')]: {
							fontSize: 'clamp(1rem, 0.9554rem + 0.2232vw, 1.125rem)'
						}
					},
					quote: {
						color: textColor,
						borderColor: theme.palette.primary.light,
						backgroundColor: 'rgba(255, 255, 255, 0.1)',
						[theme.breakpoints.up('md')]: {
							fontSize: 'clamp(1rem, 0.9554rem + 0.2232vw, 1.125rem)'
						}
					}
				}}
			/>
		);
	};

	return (
		<FadeInView>
			<Box sx={layoutSx(isImageRight)}>
				<Box sx={imageSx(isImageRight)}>
					<StrapiImageRenderer
						host={host}
						imageData={bildTextImage}
						themeMode={themeMode}
						alt={bildTextImage?.alternativeText || 'Hero Image'}
						fill
						sizes="(max-width:1120px) 100vw, (min-width:1120px) 41vw"
					/>
				</Box>

				<Stack sx={textBlockSx(isImageRight)}>
					<Typography variant="label" sx={{ color: textColor, mb: { xs: 3, lg: 4 } }}>
						{bildTextEyebrow}
					</Typography>

					<Typography variant="title-md" sx={{ color: titleColor, mb: { xs: 4, lg: 8 } }}>
						{bildTextTitle}
					</Typography>

					{renderCopy()}

					{withCta && bildTextCta?.label && (
						<Box sx={{ mt: { xs: 6, lg: 8 } }}>
							<UnderlineHoverCta
								href={ctaLink}
								label={bildTextCta.label}
								color={textColor}
								typographyVariant="body-md"
								fontWeight={500}
							/>
						</Box>
					)}
				</Stack>
			</Box>
		</FadeInView>
	);
};

export default SimpleBlock;
